import { createContext, useContext } from 'react';
import { makeProvider } from '../helpers/makeContexts';

const initCubeStore = {
  //sample data, organized by anchor table
  /*
      { 
          anchorTable1: { 
              ...dataForAnchorTable1Alone, 
              table2: { 
                  ...dataForAnchorTable1 + table2 
              }, 
              table3: { 
                  ...dataForAnchorTable1 + table3 
              }, 
          }
      } 
  */
  cubeData: {},
  initializingData: false,
  isLoadingMarketplaceSampleData: false,
  useSampleData: false,
  columnMetaDataTableSlugs: [],
  columnTableDataTableSlugs: [],
  cubeMetaData: {},
  isLoadingMetaData: false,
  currentLiveCubeAnchorSlug: null,
  currentDemoCubeAnchorSlug: null,
  /**
   * @type {Object.<string, {cubeData: Object}>}
   */
  demoCubeDataCollection: {},
  /**
   * @type {Object.<string, {cubeData: Object}>}
   */
  liveCubeDataCollection: {},
  accountCustomJoins: [],
  sampleCustomJoins: [],
  dataLoadingStatus: {},
};

function reducer(draft, { type, payload }) {
  switch (type) {
    case 'setIsLoadingMarketplaceSampleData': {
      draft.isLoadingMarketplaceSampleData = payload;
      break;
    }
    case 'setIsLoadingMetaData': {
      draft.isLoadingMetaData = payload;
      break;
    }
    case 'setCubeMetaData': {
      draft.cubeMetaData = payload;
      break;
    }
    case 'setCurrentCubeAnchorSlug': {
      if (payload.useDemoData) {
        draft.currentDemoCubeAnchorSlug = payload.anchorTableSlug;
      } else {
        draft.currentLiveCubeAnchorSlug = payload.anchorTableSlug;
      }
      break;
    }
    case 'setAnchorMetaData': {
      if (payload.useDemoData) {
        if (
          !draft.demoCubeDataCollection?.[payload.tableSlug] ||
          payload.onlyAnchorFetched
        ) {
          draft.demoCubeDataCollection[payload.tableSlug] = payload.metaData;
        }
      } else {
        if (
          !draft.liveCubeDataCollection?.[payload.tableSlug] ||
          payload.onlyAnchorFetched
        ) {
          draft.liveCubeDataCollection[payload.tableSlug] = payload.metaData;
        }
      }
      break;
    }
    case 'setJoinTableMetaData': {
      if (payload.useDemoData) {
        draft.demoCubeDataCollection[payload.anchorTableSlug][
          payload.tableSlug
        ] = payload.metaData;
      } else {
        if (!draft.liveCubeDataCollection[payload.anchorTableSlug]) {
          draft.liveCubeDataCollection[payload.anchorTableSlug] = {};
        }
        draft.liveCubeDataCollection[payload.anchorTableSlug][
          payload.tableSlug
        ] = payload.metaData;
      }
      break;
    }
    case 'updateAnchorTableMetaData': {
      if (payload.useDemoData) {
        Object.keys(payload.metaData).forEach((key) => {
          draft.demoCubeDataCollection[payload.tableSlug][key] =
            payload.metaData[key];
        });
      } else {
        Object.keys(payload.metaData).forEach((key) => {
          draft.liveCubeDataCollection[payload.tableSlug][key] =
            payload.metaData[key];
        });
      }
      break;
    }
    case 'updateNormalTableMetaData': {
      if (payload.useDemoData) {
        Object.keys(payload.metaData).forEach((key) => {
          draft.demoCubeDataCollection[payload.anchorTableSlug][
            payload.tableSlug
          ][key] = payload.metaData[key];
        });
      } else {
        Object.keys(payload.metaData).forEach((key) => {
          draft.liveCubeDataCollection[payload.anchorTableSlug][
            payload.tableSlug
          ][key] = payload.metaData[key];
        });
      }
      break;
    }
    case 'setAnchorTableData': {
      if (payload.useDemoData) {
        if (
          !draft.demoCubeDataCollection[payload.tableSlug]?.tableData ||
          payload.onlyAnchorFetched
        ) {
          draft.demoCubeDataCollection[payload.tableSlug].tableData =
            payload.tableData;
        }
      } else {
        if (
          !draft.liveCubeDataCollection[payload.tableSlug]?.tableData ||
          payload.onlyAnchorFetched ||
          payload.justUploaded
        ) {
          draft.liveCubeDataCollection[payload.tableSlug].tableData =
            payload.tableData;
        }
      }
      break;
    }
    case 'setJoinTableData': {
      if (payload.useDemoData) {
        draft.demoCubeDataCollection[payload.anchorTableSlug][
          payload.tableSlug
        ].tableData = payload.tableData;
      } else {
        draft.liveCubeDataCollection[payload.anchorTableSlug][
          payload.tableSlug
        ].tableData = payload.tableData;
      }
      break;
    }
    case 'setUseSampleData': {
      draft.useSampleData = payload;
      break;
    }
    // Modified case to update columnMetaData
    case 'setColumnMetaDataTableSlugs': {
      draft.columnMetaDataTableSlugs = payload;
      break;
    }
    case 'setColumnTableDataTableSlugs': {
      draft.columnTableDataTableSlugs = payload;
      break;
    }
    case 'setInitializingData': {
      draft.initializingData = payload;
      break;
    }
    case 'resetLiveTableData': {
      if (draft.liveCubeDataCollection && !draft.initializingData) {
        draft.liveCubeDataCollection = {};
        draft.currentLiveCubeAnchorSlug = null;
      }
      break;
    }
    case 'addFilterValueOptions': {
      if (payload.useDemoData) {
        //find the correct anchor table
        if (
          payload.anchorTableSlug &&
          draft?.demoCubeDataCollection?.[payload.anchorTableSlug]
        ) {
          //find the correct table OR apply the values to the anchor
          if (payload.tableSlug) {
            if (
              draft.demoCubeDataCollection[payload.anchorTableSlug]?.[
                payload.tableSlug
              ]
            ) {
              //add the filter value options
              draft.demoCubeDataCollection[payload.anchorTableSlug][
                payload.tableSlug
              ].filterValueOptions = payload.filterValueOptions;
            } else {
              console.error(
                `Filter values not added for ${payload.tableSlug}. Table not found.`
              );
            }
          } else if (
            payload.isAnchorTable ||
            !draft.demoCubeDataCollection[payload.anchorTableSlug]?.tableData
          ) {
            //apply filter value options for the anchor table
            draft.demoCubeDataCollection[
              payload.anchorTableSlug
            ].filterValueOptions = payload.filterValueOptions;
          }
        } else {
          console.error(
            `Filter values not added for ${payload.tableSlug}, anchor ${payload.anchorTableSlug} not found`
          );
        }
      } else {
        //find the correct anchor table
        if (
          payload.anchorTableSlug &&
          draft?.liveCubeDataCollection?.[payload.anchorTableSlug]
        ) {
          //find the correct table OR apply the values to the anchor
          if (payload.tableSlug) {
            if (
              draft.liveCubeDataCollection[payload.anchorTableSlug]?.[
                payload.tableSlug
              ]
            ) {
              //add the filter value options
              draft.liveCubeDataCollection[payload.anchorTableSlug][
                payload.tableSlug
              ].filterValueOptions = payload.filterValueOptions;
            } else {
              console.error(
                `Filter values not added for ${payload.tableSlug}. Table not found.`
              );
            }
          } else if (
            payload.isAnchorTable ||
            !draft.liveCubeDataCollection[payload.anchorTableSlug]?.tableData
          ) {
            //apply filter value options for the anchor table
            draft.liveCubeDataCollection[
              payload.anchorTableSlug
            ].filterValueOptions = payload.filterValueOptions;
          }
        } else {
          console.error(
            `Filter values not added for ${payload.tableSlug}, anchor ${payload.anchorTableSlug} not found`
          );
        }
      }
      break;
    }
    case 'setAccountCustomJoins': {
      draft.accountCustomJoins = payload;
      break;
    }
    case 'setSampleCustomJoins': {
      draft.sampleCustomJoins = payload;
      break;
    }
    case 'setManyDataStatusLoading': {
      const slugs = (payload && payload.slugs) || [];
      slugs.forEach((slug) => {
        draft.dataLoadingStatus[slug] = 'loading';
      });
      break;
    }
    case 'addDataIsLoadingStatus': {
      const { slug } = payload || {};
      if (slug) {
        draft.dataLoadingStatus[slug] = 'loading';
      }
      break;
    }
    case 'finishDataIsLoadingStatus': {
      const { slug } = payload || {};
      if (slug) {
        draft.dataLoadingStatus[slug] = 'done';
      }
      break;
    }
    default: {
      return null;
    }
  }
}

const [Context, Dispatch] = [createContext(null), createContext(null)];

export const CubeDataContextProvider = ({ init, children }) => {
  const initStore = init || initCubeStore;

  // Call makeProvider to create and return the provider component
  const ProviderComponent = makeProvider(Context, Dispatch, reducer, initStore);

  // Return the generated provider component
  return <ProviderComponent>{children}</ProviderComponent>;
};

export function useCubeData() {
  return useContext(Context);
}

export function useCubeDataDispatch() {
  return useContext(Dispatch);
}
